import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from "@angular/router";
import { BaseComponent } from "../@application/component/base.component";
import { PathEnum } from "../@application/enum/path.enum";
import { DatePipe, Location } from "@angular/common";
import { ApiBaseService } from "../../../projects/vendors/src/lib/services/api-base.service";
import { ApiEnum } from "../@application/enum/api.enum";
import { StorageService } from "../../../projects/vendors/src/lib/services/storage.service";
import { SignalRService } from "../../../projects/vendors/src/lib/services/signal-r.service";
import { NzModalService } from "ng-zorro-antd/modal";
import { Store } from "@ngrx/store";
import * as fromApp from "../app.reducer";
import { RestaurantConfig, RestaurantDetail } from "../../../projects/system-settings/src/lib/@domain/restaurant-config";
import { DailySession } from "../@domain/daily-session";
import { MatDialog } from "@angular/material/dialog";
import { DayEndDialogComponent } from "./components/day-end-dialog/day-end-dialog.component";
import { StartDayDialogComponent } from "./components/start-day-dialog/start-day-dialog.component";
import { catchError, firstValueFrom, of } from "rxjs";
import { VendorsService } from "../../../projects/vendors/src/lib/vendors.service";
import { SoundService } from "../../../projects/vendors/src/lib/services/sound.service";
import { AlertService } from "../../../projects/vendors/src/lib/services/alert.service";
import { SubscriptionDetails } from "../@domain/subscription-details";
import { HttpParams } from "@angular/common/http";
import { KeycloakService } from "keycloak-angular";
import { OnGoingOrder } from '../@domain/on-going-order';
import { QrSession } from "../../../projects/qr-order/src/lib/@domain/qr-session";
import { QrSessionConfig } from "../../../projects/qr-order/src/lib/@domain/qr-session-config";
import { QrOrderApiEnum } from "../../../projects/qr-order/src/lib/@application/enum/qr-order-api.enum";
import { ReservationApiEnum } from "../../../projects/reservation/src/lib/@application/enum/reservation-api.enum";
import { ReservationResponse } from "../../../projects/reservation/src/lib/@domain/reservation";
import { ResponsiveService } from "../@application/services/responsive.service";
import { NavigationEnd } from "@angular/router";

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrl: './home.component.scss',
    standalone: false
})
export class HomeComponent extends BaseComponent implements OnInit {
  static readonly ROUTE_DATA_BREADCRUMB = 'breadcrumb';
  breadcrumbs = {} as MenuItem;
  router = inject(Router);
  activatedRoute = inject(ActivatedRoute);
  apiBaseService = inject(ApiBaseService);
  signalrService = inject(SignalRService);
  vendorsService = inject(VendorsService);
  soundService = inject(SoundService);
  private alertService = inject(AlertService);
  dialog = inject(MatDialog)
  location = inject(Location);
  modal = inject(NzModalService)
  storageService = inject(StorageService);
  private keycloakService = inject(KeycloakService);
  private store = inject(Store<fromApp.AppState>)
  private responsiveService = inject(ResponsiveService);
  isNaveCollapsed = false;
  /** Below 992px the sidebar is an off-canvas drawer instead of a column in the flow. */
  isDrawerLayout = false;
  drawerOpen = false;
  isPhone = false;
  brandLogoUrl: string | null = null;
  themeMode: 'light' | 'dark' | 'system' = 'dark';
  private systemDarkModeQuery = window.matchMedia('(prefers-color-scheme: dark)');
  private systemThemeHandler = () => { if (this.themeMode === 'system') this.applyTheme(); };
  popOverVisible = false;
  dailySession = {} as DailySession;
  restaurantDetail = {} as RestaurantDetail;
  isDailySessionStarted = false;
  subscriptionDetails = {} as SubscriptionDetails;
  private lowBalanceWarned = false;
  private reminderTimers: ReturnType<typeof setTimeout>[] = [];
  datePipe = inject(DatePipe);
  onGoingOrder = [] as OnGoingOrder[];
  private orderNotificationSoundEnabled = true;

  get tenantName(): string {
    try {
      const tenantInfo = this.storageService.getFromSessionStorage('tenantInfo');
      if (tenantInfo && Array.isArray(tenantInfo) && tenantInfo.length > 0) {
        return tenantInfo[0].tenantName || '';
      }
    } catch (e) {
      console.error('Error fetching tenant name:', e);
    }
    return '';
  }

  get preferredUsername(): string {
    try {
      const userDetails = this.storageService.getFromSessionStorage('userDetails');
      if (userDetails && userDetails.preferredUsername) {
        return userDetails.preferredUsername;
      }
    } catch (e) {
      console.error('Error fetching preferred username:', e);
    }
    return '';
  }

  get firstName(): string {
    try {
      const userDetails = this.storageService.getFromSessionStorage('userDetails');
      return userDetails?.givenName || '';
    } catch (e) {
      console.error('Error fetching first name:', e);
    }
    return '';
  }

  get text(): string {
    return this.firstName.charAt(0).toUpperCase();
  }

  get planTypeLabel(): string {
    const type = this.subscriptionDetails?.planType;
    if (type === 1) return 'Trial';
    if (type === 2) return 'Monthly';
    if (type === 3) return 'Yearly';
    // fallback when API sends text or unknown numbers
    return typeof type === 'string' ? (type as unknown as string) : '—';
  }

  get planTypeShort(): string {
    const type = this.subscriptionDetails?.planType;
    if (type === 1) return 'T';
    if (type === 2) return 'M';
    if (type === 3) return 'Y';
    return '—';
  }

  get isLowBalance(): boolean {
    const bal = this.subscriptionDetails?.balance;
    return typeof bal === 'number' && bal < 5;
  }


  showSubscriptionInfoFallback() {
    const d = this.subscriptionDetails;
    this.modal.info({
      nzTitle: 'Subscription details',
      nzContent: `
        <div>
          <div><strong>Plan:</strong> ${this.planTypeLabel}</div>
          <div><strong>Balance:</strong> ${d?.balance ?? '—'}</div>
          <div><strong>Starts:</strong> ${d?.startDate ? new Date(d.startDate).toLocaleDateString() : '—'}</div>
          <div><strong>Ends:</strong> ${d?.endDate ? new Date(d.endDate).toLocaleDateString() : '—'}</div>
          <hr />
          <div>
            <strong>Subscription Model (Points-Based)</strong>
            <div style="margin-top: 6px;">
              <ul style="margin: 4px 0 0 18px;">
                <li>1 point = 1 day of system access.</li>
                <li>Once points reach zero, access will be suspended until new points are purchased.</li>
                <li>The Client may purchase additional points at any time before the current points expire.</li>
                <li>The number of points purchased determines the number of days the system can be accessed.</li>
              </ul>
            </div>
          </div>
        </div>
      `,
      nzOkText: 'Close'
    });
  }

  get isDarkMode(): boolean {
    if (this.themeMode === 'system') {
      return this.systemDarkModeQuery.matches;
    }
    return this.themeMode === 'dark';
  }

  get currentThemeLabel(): string {
    return this.themeMode.charAt(0).toUpperCase() + this.themeMode.slice(1);
  }

  setTheme(mode: 'light' | 'dark' | 'system'): void {
    this.themeMode = mode;
    localStorage.setItem('theme', mode);
    this.applyTheme();
  }

  toggleDarkMode(): void {
    this.setTheme(this.isDarkMode ? 'light' : 'dark');
  }

  toggleSidebar(): void {
    // In drawer layout the same control slides the sidebar in/out instead of
    // narrowing it — a 64px rail is useless on a phone.
    if (this.isDrawerLayout) {
      this.drawerOpen = !this.drawerOpen;
      return;
    }
    this.isNaveCollapsed = !this.isNaveCollapsed;
    localStorage.setItem('sidebarCollapsed', String(this.isNaveCollapsed));
  }

  closeDrawer(): void {
    this.drawerOpen = false;
  }

  private applyTheme(): void {
    const effectiveTheme = this.isDarkMode ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', effectiveTheme);
    document.body.classList.toggle('dark-theme', this.isDarkMode);
  }

  ngOnInit(): void {
    const savedTheme = localStorage.getItem('theme') as 'light' | 'dark' | 'system' | null;
    this.themeMode = savedTheme || 'dark';
    this.applyTheme();
    this.systemDarkModeQuery.addEventListener('change', this.systemThemeHandler);

    this.brandLogoUrl = sessionStorage.getItem('logoUrl');

    this.signalrService.startConnection().then()
    this.subscription$.add(
      this.router.events.subscribe(event => {
        const length = this.createBreadcrumbs(this.activatedRoute.root).length;
        this.breadcrumbs = this.createBreadcrumbs(this.activatedRoute.root)[length - 1];
        // Navigating from the drawer should close it, otherwise it covers the page it opened.
        if (event instanceof NavigationEnd) this.drawerOpen = false;
      }));
    this.isNaveCollapsed = localStorage.getItem('sidebarCollapsed') === 'true';

    this.subscription$.add(
      this.responsiveService.isDrawerLayout$.subscribe(matches => {
        this.isDrawerLayout = matches;
        if (matches) {
          // A 64px icon rail is useless on touch (labels are hover-tooltips), so the
          // drawer always shows full-width labels. Desktop preference is kept in
          // localStorage and restored below.
          this.isNaveCollapsed = false;
        } else {
          this.drawerOpen = false;
          this.isNaveCollapsed = localStorage.getItem('sidebarCollapsed') === 'true';
        }
      })
    );
    this.subscription$.add(
      this.responsiveService.isPhone$.subscribe(matches => this.isPhone = matches)
    );

    this.getSubscriptionDetails();
    this.getRestaurantDetail();
    this.getAllOngoingOrder();
    this.getDailySession().then();
    this.loadQrNotificationSoundSetting();
    this.subscription$.add(
      this.vendorsService.dayEndBySettingsSubject.subscribe({
        next: value => {
          if (value)
            this.getDailySession().then();
        }
      })
    )
    this.subscription$.add(
      this.vendorsService.qrOrderRequestSubject.subscribe({
        next: value => {
          if (value) {
            this.checkActiveQrSessions();
          }
        }
      })
    )
    this.subscription$.add(
      this.vendorsService.reservationNotificationSubject.subscribe({
        next: value => {
          if (value) {
            this.checkPendingReservations();
            this.scheduleReservationReminders();
          }
        }
      })
    )
    this.subscription$.add(
      this.vendorsService.callWaiterSubject.subscribe({
        next: data => {
          if (this.orderNotificationSoundEnabled) {
            this.soundService.playSound();
          }
          this.alertService.showPersistentInfo('Waiter Request', `${data.tableName} is requesting a waiter`);
        }
      })
    )
    this.scheduleReservationReminders();
  }

  private getSubscriptionDetails() {
    const tenantId = this.storageService.getFromSessionStorage('tenantId');
    const param = new HttpParams()
      .append('tenantId', tenantId)
    this.apiBaseService.GET<SubscriptionDetails>([ApiEnum.Subscriptions, ApiEnum.Tenant, ApiEnum.Active], true, true, param)
      .subscribe({
        next: value => {
          this.subscriptionDetails = value;
          const bal = value?.balance;
          if (!this.lowBalanceWarned && typeof bal === 'number' && bal < 5) {
            this.lowBalanceWarned = true;
            this.modal.warning({
              nzTitle: 'Low subscription balance',
              nzContent: `Your subscription balance is below 5. Please top up before ${this.datePipe.transform(this.subscriptionDetails?.endDate, 'longDate')} to continue uninterrupted service.`,
              nzOkText: 'OK'
            });
          }
        }
      })
  }

  get subscriptionTooltip(): string {
    return `1 token per day. Your ${this.planTypeLabel} plan expires on ${this.datePipe.transform(this.subscriptionDetails?.endDate, 'longDate')}.`;
  }

  /**
   * Read lazily, not latched: 'restaurantDetail' is written by an async GET, so a latched
   * read on a hard refresh would miss it. Absent key means the flag predates this setting,
   * which is on.
   */
  get customerAppEnabled(): boolean {
    return this.storageService.getFromSessionStorage('restaurantDetail')?.customerAppEnabled ?? true;
  }

  private checkActiveQrSessions(): void {
    if (!this.customerAppEnabled) return;
    const params = new HttpParams().set('$filter', `status eq 'Active'`);
    this.apiBaseService.GET<QrSession[]>([
      QrOrderApiEnum.PosConfigModule,
      QrOrderApiEnum.QrSession
    ], false, true, params).pipe(
      catchError(() => of([]))
    ).subscribe({
      next: sessions => {
        if (sessions && sessions.length > 0) {
          if (this.orderNotificationSoundEnabled) {
            this.soundService.playSoundLoop();
          }
          this.modal.confirm({
            nzTitle: 'QR Order Request',
            nzContent: 'There are QR sessions available that require action.',
            nzOkText: 'View',
            nzCancelText: 'Cancel',
            nzOnOk: () => {
              this.soundService.stopSound();
              this.router.navigate([PathEnum.Home, PathEnum.QrOrders], { queryParams: { tab: 'approvals' } });
            },
            nzOnCancel: () => {
              this.soundService.stopSound();
            }
          });
        }
      }
    });
  }

  private scheduleReservationReminders(): void {
    this.reminderTimers.forEach(t => clearTimeout(t));
    this.reminderTimers = [];
    if (!this.customerAppEnabled) return;

    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

    const params = new HttpParams()
      .set('$filter', `status eq 'Approved' and reservationDateTime ge ${todayStart.toISOString()} and reservationDateTime le ${todayEnd.toISOString()}`);

    this.apiBaseService.GET<ReservationResponse[]>([
      ReservationApiEnum.PosConfigModule,
      ReservationApiEnum.Reservations
    ], false, true, params).pipe(
      catchError(() => of([] as ReservationResponse[]))
    ).subscribe({
      next: reservations => {
        const reminders: { minutes: number; label: string }[] = [
          { minutes: 120, label: '2 hours' },
          { minutes: 60, label: '1 hour' },
          { minutes: 30, label: '30 minutes' }
        ];

        for (const r of reservations ?? []) {
          const resTime = new Date(r.reservationDateTime);
          const resTimeStr = resTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });

          for (const reminder of reminders) {
            const triggerAt = resTime.getTime() - reminder.minutes * 60 * 1000;
            const delay = triggerAt - Date.now();
            if (delay > 0) {
              const timer = setTimeout(() => {
                this.soundService.playSound();
                this.alertService.showPersistentInfo(
                  'Reservation Reminder',
                  `Reservation for ${r.customerName} (${r.pax} pax) at ${r.tableName} in ${reminder.label}. Reserved time: ${resTimeStr}`
                );
              }, delay);
              this.reminderTimers.push(timer);
            }
          }
        }
      }
    });
  }

  private checkPendingReservations(): void {
    if (!this.customerAppEnabled) return;
    const params = new HttpParams().set('$filter', `status eq 'Pending'`);
    this.apiBaseService.GET<ReservationResponse[]>([
      ReservationApiEnum.PosConfigModule,
      ReservationApiEnum.Reservations
    ], false, true, params).pipe(
      catchError(() => of([] as ReservationResponse[]))
    ).subscribe({
      next: reservations => {
        const pending = reservations ?? [];
        if (pending.length > 0) {
          this.soundService.playSoundLoop();
          this.modal.confirm({
            nzTitle: 'New Reservation Request',
            nzContent: `There are ${pending.length} pending reservation(s) that require action.`,
            nzOkText: 'View',
            nzCancelText: 'Cancel',
            nzOnOk: () => {
              this.soundService.stopSound();
              this.router.navigate([PathEnum.Home, PathEnum.Reservations]);
            },
            nzOnCancel: () => {
              this.soundService.stopSound();
            }
          });
        }
      }
    });
  }

  private loadQrNotificationSoundSetting(): void {
    this.apiBaseService.GET<QrSessionConfig>([
      QrOrderApiEnum.PosConfigModule,
      QrOrderApiEnum.SystemSetting,
      QrOrderApiEnum.QrSession
    ], false, true).pipe(
      catchError(() => of(null))
    ).subscribe(config => {
      this.orderNotificationSoundEnabled = config?.qrSessionConfig?.enableOrderNotificationSound ?? true;
    });
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.soundService.stopSound();
    this.reminderTimers.forEach(t => clearTimeout(t));
    this.reminderTimers = [];
    this.systemDarkModeQuery.removeEventListener('change', this.systemThemeHandler);
  }

  onLogout() {
    this.keycloakService.logout(window.location.origin);
  }




  showOnLogoutConfirmation() {
    const confirmModal = this.modal.confirm({
      nzTitle: 'Do you Want to logout?',
      nzOnOk: () => this.performLogout()

    });
  }

  OnDashboard() {
    this.router.navigate([PathEnum.Home, PathEnum.DashBoard]);

  }


  // @ts-ignore
  private createBreadcrumbs(route: ActivatedRoute, url: string = '', breadcrumbs: MenuItem[] = []): MenuItem[] {
    const children: ActivatedRoute[] = route.children;

    if (children.length === 0) {
      return breadcrumbs;
    }

    for (const child of children) {
      const routeURL: string = child.snapshot.url.map(segment => segment.path).join('/');
      if (routeURL !== '') {
        url += `/${routeURL}`;
      }

      const label = child.snapshot.data[HomeComponent.ROUTE_DATA_BREADCRUMB];
      if (label != null || label != undefined) {
        breadcrumbs.push({ label, url });
      }
      return this.createBreadcrumbs(child, url, breadcrumbs);
    }
  }

  private getAllOngoingOrder(): void {
    this.apiBaseService.GET<OnGoingOrder[]>([ApiEnum.RestaurantModule, ApiEnum.Cart, ApiEnum.GetAll])
      .subscribe({
        next: response => {
          this.onGoingOrder = response
        }
      });
  }

  private getRestaurantDetail() {
    this.apiBaseService.GET<RestaurantConfig>([ApiEnum.RestaurantModule, ApiEnum.SystemSetting,
    ApiEnum.RestaurantDetail])
      .subscribe({
        next: value => {
          if (value.restaurantDetail) {
            this.restaurantDetail = value.restaurantDetail;
            this.storageService.saveToSessionStorage('restaurantDetail', value.restaurantDetail);
          }
        }
      })
  }

  private isDailySessionBelongsToToday(date: Date): boolean {
    if (this.isDailySessionStarted) {
      const endTimeParts = this.restaurantDetail.dayEndInfo.endTimeIn24H.split(':').map(Number);
      let closedTime = new Date(date.getFullYear(), date.getMonth(), date.getDate(), ...endTimeParts);

      if (!this.restaurantDetail.dayEndInfo.endInSameDay) {
        closedTime.setDate(closedTime.getDate() + 1);
      }
      return closedTime >= new Date();
    } else {
      return true;
    }
  }

  private async performLogout(): Promise<void> {
    this.clearClientCache();
    try {
      await this.keycloakService.logout(`${window.location.origin}/${PathEnum.Auth}`);
    } catch (error) {
      console.error('Keycloak logout failed, falling back to client navigation', error);
      await this.router.navigate([PathEnum.Auth]);
    }
  }

  private clearClientCache(): void {
    sessionStorage.clear();
    localStorage.clear();
    document.cookie.split(";").forEach((cookie) => {
      const name = cookie.split("=")[0].trim();
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
    });
  }

  onBack() {
    if (this.router.url !== `/${PathEnum.Home}/${PathEnum.DashBoard}`) {
      this.location.back();
    }
  }

  async getDailySession(): Promise<void> {
    const value = await firstValueFrom(
      this.apiBaseService.GET<DailySession>([ApiEnum.RestaurantModule, ApiEnum.DailySession])
    );

    if (value) {
      this.dailySession = value;
      this.isDailySessionStarted = value.id !== '00000000-0000-0000-0000-000000000000';
      this.storageService.saveToSessionStorage('dailySession', value);
    }
  }


  async openStartDayDialog(): Promise<boolean> {
    // let the focus trap land on the cash input (nzId sets the id on the inner input)
    const ref = this.dialog.open(StartDayDialogComponent, { width: '400px', autoFocus: '#startCash' });
    // dialog closes with the created session on success, undefined on cancel
    const value = await firstValueFrom(ref.afterClosed());
    // cancel changes nothing server-side, so only refetch when a session was actually started
    if (value) {
      await this.getDailySession();
    }
    return !!value;
  }


  async onDayEnd(): Promise<any> {
    // Prompt to confirm closing the previous session and starting a new one
    const confirmed = await new Promise<boolean>((resolve) => {
      this.modal.confirm({
        nzTitle: 'End Day & Start New Session',
        nzContent: 'Do you want to close the previous session and start a new day?',
        nzOkText: 'Yes, Continue',
        nzCancelText: 'Cancel',
        nzOnOk: () => resolve(true),
        nzOnCancel: () => resolve(false)
      });
    });

    if (!confirmed) {
      return false;
    }

    // Close the previous session
    const ref = this.dialog.open(DayEndDialogComponent, {
      width: '400px',
      // let the focus trap land on the cash input (nzId sets the id on the inner input)
      autoFocus: '#dayEndCashAmount',
      data: { dailySession: this.dailySession }
    });

    // dialog closes with the ended session on success, undefined on cancel
    const dayEnded = await firstValueFrom(ref.afterClosed());

    if (!dayEnded) {
      // cancelled - nothing changed, no refetch needed
      return false;
    }

    // openStartDayDialog refreshes the session itself
    return this.openStartDayDialog();
  }

  async onPos() {

    await this.checkForDailySession();

    if (this.isDailySessionStarted && this.isDailySessionBelongsToToday(new Date(this.dailySession.startTime))) {
      this.router.navigate([PathEnum.Home, PathEnum.PointOfSale]).then();
    }
  }

  async onNewOrder() {
    await this.checkForDailySession();

    if (this.isDailySessionStarted && this.isDailySessionBelongsToToday(new Date(this.dailySession.startTime))) {
      this.router.navigate([PathEnum.Home, PathEnum.TableOccupancy]).then();
    }
  }

  private async checkForDailySession(): Promise<void> {
    if (!this.isDailySessionStarted || !this.isDailySessionBelongsToToday(new Date(this.dailySession.startTime))) {
      if (!this.isDailySessionBelongsToToday(new Date(this.dailySession.startTime))) {
        await this.dayEndStartPrompt();
      } else {
        await this.openStartDayDialog();
      }
    }
  }


  private dayEndStartPrompt(): Promise<void> {
    return new Promise((resolve) => {
      this.modal.warning({
        nzTitle: 'Warning',
        nzContent: 'Daily session does not belong to today. Please start a new daily session.',
        nzOkText: 'Start Day',
        nzOnOk: async () => {
          // onDayEnd chains into the start-day dialog itself when the day is ended.
          // If the user cancels either step, cancel means cancel - don't force start-day.
          await this.onDayEnd();
          resolve();
        }
      });
    });
  }

}

export interface MenuItem {
  label: string;
  url: string;
}
